/**
 * Radial vignette, warm-tinted rather than black.
 *
 * A pure-black vignette on a plum-black felt reads as a dirty lens. Instead the
 * corners are multiplied by a tint derived from the scene's own materials — the
 * deep felt colour warmed by the key light — so the falloff looks like the room
 * running out of light rather than the image running out of exposure. Shading
 * and shadows already come from `LIGHT`; this keeps the frame edge in the same
 * conversation.
 *
 * The 0..1 coordinate is taken across the *filter area* (the letterboxed design
 * rect), not the padded filter texture, so the vignette stays centred on the
 * table at every aspect ratio, including mobile portrait.
 */

import { Filter, GlProgram, defaultFilterVert } from 'pixi.js';

import { DESIGN, LIGHT } from '@/pixi/layout';
import { palette } from '@/core/tokens';

import { GLSL_FRAME, GLSL_GLOBALS, mixRgb, rgb01, vec3f } from './glsl';

const fragment = /* glsl */ `
in vec2 vTextureCoord;
out vec4 finalColor;

uniform sampler2D uTexture;
${GLSL_GLOBALS}
uniform float uIntensity;
uniform float uRadius;
uniform float uSoftness;
uniform vec2 uCentre;
uniform vec3 uTint;

${GLSL_FRAME}

void main(void) {
    vec4 src = texture(uTexture, vTextureCoord);

    // Position across the filtered area, corrected so the falloff is circular
    // rather than stretched with the viewport.
    // Biased toward the key light, not centred on the frame.
    //
    // This file's own thesis is that the falloff should look like the room
    // running out of light rather than the image running out of exposure. But a
    // perfectly frame-centred, perfectly symmetric falloff is BY DEFINITION the
    // image running out of exposure — it is the one shape no light source can
    // produce. Every other element in the scene derives from LIGHT; this was
    // the only one that ignored it. Pulling the centre 30% of the way toward
    // the lamp's projection darkens the lower-right more than the upper-left,
    // which is what a key at upper-left actually does.
    vec2 d = (frameUv(vTextureCoord) - uCentre) * vec2(frameAspect(), 1.0);
    float r = length(d) / max(uRadius, 1e-3);

    // A faint continuous term through the middle. The smoothstep alone is
    // exactly flat across the whole centre of the frame, and real vignetting
    // falls off continuously from the axis — the flat region is what gives an
    // otherwise good vignette a sticker-like quality.
    float fall = smoothstep(1.0 - uSoftness, 1.0 + uSoftness, r) + 0.06 * r * r;
    vec3 shade = mix(vec3(1.0), uTint, clamp(fall, 0.0, 1.0) * uIntensity);

    // Multiplying survives premultiplied alpha untouched.
    finalColor = vec4(src.rgb * shade, src.a);
}
`;

export interface VignetteFilterOptions {
  /** 0 = off, 1 = corners fully at the tint colour. */
  intensity?: number;
  /** Distance (in half-frame units) at which the falloff is halfway. */
  radius?: number;
  /** Width of the falloff. Larger = gentler. */
  softness?: number;
  /** Multiplier applied at full falloff. Defaults to a warmed deep-felt tone. */
  tint?: number;
}

/**
 * The corners fall toward the felt's own shadow colour lifted by the key light,
 * which lands near a warm neutral — it darkens without turning the frame edge
 * grey or magenta.
 */
const DEFAULT_TINT = mixRgb(rgb01(palette.feltDeep), rgb01(LIGHT.colour), 0.34);

const DEFAULTS: Required<Omit<VignetteFilterOptions, 'tint'>> = {
  intensity: 0.62,
  radius: 0.78,
  softness: 0.62,
};

interface VignetteUniforms {
  uIntensity: number;
  uRadius: number;
  uSoftness: number;
  uTint: Float32Array;
}

export class VignetteFilter extends Filter {
  readonly #uniforms: VignetteUniforms;

  constructor(options: VignetteFilterOptions = {}) {
    const o = { ...DEFAULTS, ...options };

    super({
      glProgram: GlProgram.from({
        vertex: defaultFilterVert,
        fragment,
        name: 'wild21-vignette',
        preferredFragmentPrecision: 'highp',
      }),
      resources: {
        vignetteUniforms: {
          uIntensity: { value: o.intensity, type: 'f32' },
          uRadius: { value: o.radius, type: 'f32' },
          uSoftness: { value: o.softness, type: 'f32' },
          // The key's projection onto the design rect, pulled 30% of the way
          // from frame centre. Derived from LIGHT so it cannot drift.
          uCentre: {
            value: new Float32Array([
              0.5 + 0.3 * (LIGHT.x / DESIGN.width - 0.5),
              0.5 + 0.3 * (LIGHT.y / DESIGN.height - 0.5),
            ]),
            type: 'vec2<f32>',
          },
          uTint: {
            value: vec3f(options.tint === undefined ? DEFAULT_TINT : rgb01(options.tint)),
            type: 'vec3<f32>',
          },
        },
      },
      resolution: 'inherit',
      antialias: 'inherit',
      padding: 0,
    });

    this.#uniforms = this.resources.vignetteUniforms.uniforms as VignetteUniforms;
  }

  get intensity(): number {
    return this.#uniforms.uIntensity;
  }
  set intensity(value: number) {
    this.#uniforms.uIntensity = Math.max(0, value);
  }

  get radius(): number {
    return this.#uniforms.uRadius;
  }
  set radius(value: number) {
    this.#uniforms.uRadius = value;
  }

  get softness(): number {
    return this.#uniforms.uSoftness;
  }
  set softness(value: number) {
    this.#uniforms.uSoftness = value;
  }

  /** Replace the falloff tint. Accepts a 0xRRGGBB token value. */
  setTint(hex: number): void {
    const [r, g, b] = rgb01(hex);
    const tint = this.#uniforms.uTint;
    tint[0] = r;
    tint[1] = g;
    tint[2] = b;
  }
}
